import { css } from 'styled-components';

/**
 * Pulsating attention ring, rendered as a `::before` overlay on the target
 * element (which it makes `position: relative`). Used to draw the eye to a
 * field — e.g. the role select in UserModal, or a consequence formula input in
 * ScenarioForm when opened from a "missing variable" prompt.
 *
 * `pointer-events: none` keeps the ring from swallowing clicks on the target.
 *
 * Pass a `selector` when the target isn't the styled element itself — this keeps
 * the `@keyframes` at the top level of the enclosing template (nesting it inside
 * a rule is invalid CSS). Embed at the TOP level of a styled-component, gated on
 * whatever "active" flag applies:
 *   ${({ $active }) => $active && pulseHighlight()}              // rings the element itself
 *   ${({ $active }) => $active && pulseHighlight('.fi-root')}    // rings a child
 */
export const pulseHighlight = (selector = '&', { radius = 8, inset = -4 }: { radius?: number; inset?: number } = {}) =>
  css`
    ${selector} {
      position: relative;
    }

    ${selector}::before {
      content: '';
      position: absolute;
      inset: ${inset}px;
      border-radius: ${radius}px;
      border: 2px solid ${({ theme }) => theme.colors.gp1};
      pointer-events: none;
      animation: pulseHighlightRing 1.5s ease-in-out infinite;
    }

    @keyframes pulseHighlightRing {
      0%,
      100% {
        opacity: 0.4;
        box-shadow: 0 0 8px ${({ theme }) => theme.colors.gp1}40;
      }
      50% {
        opacity: 1;
        box-shadow: 0 0 16px ${({ theme }) => theme.colors.gp1}80;
      }
    }
  `;
